
const FilterPanel = ({ filters = {}, onFilterChange = () => {}, onClearFilters, isMobile, isVisible, onClose }) => {
    const handleSearchKeyDown = (e) => {
      if (e.key === 'Enter') {
        onClose();
      }
    };

    const handleSearchSubmit = () => {
      onClose();
    };

    const handleSearchChange = (value) => {
      onFilterChange('search', value);
    };

    return (
      <div
        className={`${isMobile ? 'fixed inset-0 z-50 bg-black bg-opacity-50' : 'w-full'} ${isMobile && !isVisible ? 'hidden' : ''} ${isMobile ? 'mt-4' : 'mt-[10px] mr-8 border rounded-10'}`}
        onClick={isMobile ? onClose : undefined}
      >
        <div
          className={`${isMobile ? 'absolute right-0 top-0 h-full w-80 max-w-[90vw]' : 'w-full'} bg-pl_bg_light_4 p-3 sm:p-4 border-l border-pl_border_light_1 overflow-y-auto`}
          onClick={e => e.stopPropagation()}
        >
          {/* Always-visible Close Button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1 text-pl_bg_light_3 bg-red-500 hover:bg-red-600 rounded-10 shadow-lg focus:outline-none z-20 border-2 border-pl_bg_light_3 transition"
            aria-label="Close filter panel"
            type="button"
          >
            <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {/* Mobile Header */}
          {isMobile && (
            <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 border-b border-pl_border_light_1">
              <h3 className="text-base sm:text-lg font-semibold text-pl_txt_light_1">Search and filter</h3>
              <button
                onClick={onClose}
                className="p-1 sm:p-2 text-pl_txt_light_2 hover:text-pl_txt_light_1"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          
          {/* Desktop Header */}
          {!isMobile && (
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-pl_txt_light_1">Search and filter</h3>
          )}
          
          <div className="space-y-3 sm:space-y-4">
            {/* Search Bar */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-pl_txt_light_1 mb-1">Search Text</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={filters.search || ''}
                  onChange={e => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Type to search in logs..."
                  className="flex-1 p-2 sm:p-2.5 border border-pl_border_light_1 rounded text-xs sm:text-sm focus:ring-2 focus:ring-pl_prim_color_1 focus:border-pl_prim_color_1 bg-pl_bg_light_3 text-pl_txt_light_1"
                />
                <button
                  onClick={handleSearchSubmit}
                  className="px-3 py-2 sm:py-2.5 bg-pl_prim_color_1 text-white rounded text-xs sm:text-sm hover:bg-pl_prim_color_3 transition-colors focus:ring-2 focus:ring-pl_prim_color_1 focus:outline-none"
                  type="button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-pl_txt_light_2 mt-1">Search matches text content and highlights results. Press Enter or click search to close panel.</p>
            </div>

            {/* Max Logs Limit */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-pl_txt_light_1 mb-1">Max Logs to Show</label>
              <select
                value={filters.limit || 50}
                onChange={(e) => onFilterChange('limit', parseInt(e.target.value))}
                className="w-full p-2 sm:p-2.5 border border-pl_border_light_1 rounded text-xs sm:text-sm focus:ring-2 focus:ring-pl_prim_color_1 focus:border-pl_prim_color_1 bg-pl_bg_light_3 text-pl_txt_light_1"
              >
                <option value={25}>25 logs</option>
                <option value={50}>50 logs</option>
                <option value={100}>100 logs</option>
                <option value={200}>200 logs</option>
                <option value={500}>500 logs</option>
              </select>
            </div>

            {/* Recent Logs Checkbox */}
            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.recentLogs}
                  onChange={(e) => onFilterChange('recentLogs', e.target.checked)}
                  className="rounded border-pl_border_light_1 text-pl_prim_color_1 focus:ring-pl_prim_color_1 w-4 h-4 sm:w-5 sm:h-5"
                />
                <span className="text-xs sm:text-sm text-pl_txt_light_1">Recent logs (latest first)</span>
              </label>
            </div>
  
            {/* Date Filters */}
            <div className="space-y-2 sm:space-y-3">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-pl_txt_light_1 mb-1">
                  From Date
                  <span className="text-pl_txt_light_2 ml-1">(shows from this date to today)</span>
                </label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => onFilterChange('fromDate', e.target.value)}
                  className="w-full p-2 sm:p-2.5 border border-pl_border_light_1 rounded text-xs sm:text-sm focus:ring-2 focus:ring-pl_prim_color_1 focus:border-pl_prim_color_1 bg-pl_bg_light_3 text-pl_txt_light_1"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-pl_txt_light_1 mb-1">
                  To Date
                  <span className="text-pl_txt_light_2 ml-1">(shows only this specific day)</span>
                </label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => onFilterChange('toDate', e.target.value)}
                  className="w-full p-2 sm:p-2.5 border border-pl_border_light_1 rounded text-xs sm:text-sm focus:ring-2 focus:ring-pl_prim_color_1 focus:border-pl_prim_color_1 bg-pl_bg_light_3 text-pl_txt_light_1"
                  max={new Date().toISOString().split('T')[0]}
                  min={filters.fromDate}
                />
              </div>
            </div>
  
            {/* Status Dropdown */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-pl_txt_light_1 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => onFilterChange('status', e.target.value)}
                className="w-full p-2 sm:p-2.5 border border-pl_border_light_1 rounded text-xs sm:text-sm focus:ring-2 focus:ring-pl_prim_color_1 focus:border-pl_prim_color_1 bg-pl_bg_light_3 text-pl_txt_light_1"
              >
                <option value="all">All Statuses</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Filter Info
            <div className="bg-pl_bg_light_5 border border-pl_border_light_1 rounded-10 p-2 sm:p-3">
              <h4 className="text-xs sm:text-sm font-semibold text-pl_prim_color_1 mb-1">Filter Tips:</h4>
              <ul className="text-xs text-pl_txt_light_2 space-y-1">
                <li>• <strong>From Date:</strong> Shows logs from selected date to today</li>
                <li>• <strong>To Date:</strong> Shows logs only for that specific day</li>
                <li>• <strong>Recent Logs:</strong> Ignores date filters, shows latest logs</li>
                <li>• <strong>Max Logs:</strong> Controls how many logs to display</li>
              </ul>
            </div> */}
  
            {/* Clear Filters Button */}
            <div className="pt-1 sm:pt-2">
              <button
                onClick={onClearFilters}
                className="w-full px-3 py-2 sm:py-2.5 bg-pl_border_light_1 text-pl_txt_light_1 rounded text-xs sm:text-sm hover:bg-pl_border_light_2 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  export default FilterPanel;

  